const CHUNK_SIZE = 1024 * 1024 * 50; // 50 mb

// A chunk that fails on the way (network drop, gateway timeout, a restarting
// server) is sent again, the pause growing with every attempt. A rejected chunk
// (validation error) is not - sending it again gives the same answer.
const MAX_ATTEMPTS = 3;
const RETRY_DELAY = 2000;

/**
 * Sends a file to a chunk upload endpoint (the SCORM one or the chapter file one)
 * one chunk after another.
 *
 * The backend stores every chunk under the hash and its index and assembles
 * them later by the index, so the caller keeps the hash to refer to the upload.
 */
export default class {
    constructor (Upload, $q, $timeout, $translate) {
        this.Upload = Upload;
        this.$q = $q;
        this.$timeout = $timeout;
        this.$translate = $translate;
    }

    /**
     * Resolves with the number of chunks once the last one is in. A failed chunk
     * stops the ones behind it - otherwise the assembled file is silently
     * truncated. An empty file still counts as one chunk, which is what the
     * upload endpoints expect to receive. Progress is reported in bytes of the
     * whole file.
     */
    upload (file, url, hash, onProgress) {
        const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        let chain = this.$q.resolve();

        for (let index = 0; index < totalChunks; index++) {
            const offset = index * CHUNK_SIZE;
            const chunk = {
                url,
                // a file dropped into the TinyMCE iframe belongs to the iframe window, and ng-file-upload
                // checks "instanceof window.Blob", so it sent the chunk as a plain object without the content
                file: new Blob([file.slice(offset, offset + CHUNK_SIZE)]),
                filename: file.name,
                hash,
                index: index + 1,
                totalChunks,
                // the chunks go out one at a time, so everything before this one
                // is already on the server
                onProgress: loaded => onProgress(offset + loaded)
            };

            chain = chain.then(() => this.uploadChunk(chunk, 1));
        }

        return chain.then(() => totalChunks);
    }

    uploadChunk (chunk, attempt) {
        chunk.file.name = chunk.filename;

        return this.Upload.upload({
            url: chunk.url,
            method: 'POST',
            data: {
                file: chunk.file,
                filename: chunk.filename,
                hash: chunk.hash,
                index: chunk.index,
                totalCunks: chunk.totalChunks
            }
        })
            // not .progress(): it hands every event over twice, the second copy a moment later, and a late
            // copy of the previous chunk moves the bar back
            .then(null, null, event => chunk.onProgress(event.loaded))
            .catch((response) => {
                if (attempt >= MAX_ATTEMPTS || !this.isRetryable(response)) {
                    return this.$q.reject(response);
                }

                chunk.onProgress(0);

                return this.$timeout(() => this.uploadChunk(chunk, attempt + 1), RETRY_DELAY * attempt);
            });
    }

    /**
     * No status means the request never got an answer (connection lost, aborted).
     */
    isRetryable (response) {
        const status = response ? response.status : 0;

        return status <= 0 || status === 408 || status === 429 || status >= 500;
    }

    /**
     * The reason an upload failed, as the backend put it: a list of field errors
     * from a rejected chunk, or the error of a rejected file.
     */
    getErrorMessage (response) {
        const data = response && response.data;

        if (Array.isArray(data) && data.length && data[0].message) {
            return data[0].message;
        }

        if (data && data.error && data.error.message) {
            return data.error.message;
        }

        if (data && data.message) {
            return data.message;
        }

        return this.$translate.instant('upload.Upload failed');
    }
}



// WEBPACK FOOTER //
// ./src/app/services/chunkUpload/index.js